/**
 * What the trigger form starts with: a fresh trigger, a fresh target, and a
 * stored record read back into form values.
 *
 * `triggerRecordToFormValues` is the inverse of `buildTriggerRequest`: a
 * page that reseeds a stored trigger even slightly differently would rewrite
 * it on the next save.
 */

import { invocationSpecValuesFromWire } from "@/shared/components/invocationSpec/invocationSpecFromWire";
import {
  defaultInvocationSpecValues,
  type InvocationSpecValues,
} from "@/shared/components/invocationSpec/invocationSpecSchema";
import { browserTimeZone } from "@/shared/datetime";
import {
  ACCEPT_ALL_PATTERN,
  entityOfSubscription,
  eventLabel,
  isEventSubscription,
  isTriggeringSession,
  newScheduleDraft,
  OncePer,
  scheduleFromUtcCron,
  ThreadVisibility,
  TriggerSourceType,
  TriggerTargetType,
  type PlatformEventType,
  type TriggerTargetSpec,
  type TriggerRecord,
} from "@/shared/domain/triggers";

import { conditionToValues, emptyConditionValues } from "./conditionValues";
import {
  DEFAULT_TARGET_KIND,
  suppliedInputFor,
  TRIGGER_FORM_ENTITIES,
  type TriggerFormEntity,
} from "./formScope";
import { gateConditionFor, sharedGateOf } from "./gateCondition";
import {
  type EventSubscriptionValues,
  type ScheduleValues,
  type TargetFormValues,
  type TriggerFormValues,
} from "./triggerFormSchema";

/** A client-side id for a target the author just added. */
export function newTargetId(): string {
  return `tgt_${crypto.randomUUID().slice(0, 8)}`;
}

/** What a new target is born into: the trigger's source, for sensible defaults. */
export interface TargetDraftContext {
  events: readonly PlatformEventType[];
  firesOnSchedule: boolean;
}

/**
 * The message a Slack target starts with: the change and the subject's most
 * recognizable field, so a target added and saved as-is already says
 * something useful. Empty when there is no subject to name yet.
 */
export function defaultSlackMessage(context: TargetDraftContext): string {
  if (context.firesOnSchedule) {
    return "{{trigger.name}} ran on schedule at {{schedule.scheduled_for}}";
  }
  const entity = entityOfSubscription(context.events);
  if (entity === null) {
    return "";
  }
  const label = context.events.map(eventLabel).join(" / ");
  switch (entity) {
    case "file":
      return `${label}: {{file.relative_path}} in {{dataset.name}}`;
    case "dataset":
      return `${label}: {{dataset.name}} ({{dataset.dataset_id}})`;
    case "session":
      return `${label}: {{session.name}} ({{session.session_id}})`;
    case "invocation":
      return `${label}: {{action.name}} ({{invocation.invocation_id}})`;
    case "event":
      return `${label}: {{event.name}} on {{session.name}} ({{event.event_id}})`;
    case "trigger":
      return label;
  }
}

/** A subscription nobody has checked a change on yet. */
export function defaultEventSubscriptionValues(): EventSubscriptionValues {
  return {
    type: TriggerSourceType.Events,
    entity: TRIGGER_FORM_ENTITIES[0],
    events: [],
    oncePer: OncePer.Occurrence,
  };
}

/** A schedule nobody has edited yet, stated in `timeZone`. */
export function defaultScheduleValues(
  timeZone: string = browserTimeZone(),
): ScheduleValues {
  return { type: TriggerSourceType.Schedule, ...newScheduleDraft(timeZone) };
}

/** A trigger nobody has said anything about yet, on one empty target. */
export function defaultTriggerFormValues(): TriggerFormValues {
  return {
    name: "",
    enabled: true,
    firesOn: defaultEventSubscriptionValues(),
    condition: emptyConditionValues(),
    targets: [newTargetValues(DEFAULT_TARGET_KIND)],
  };
}

/** A target of `type`, born into the trigger's source for sensible defaults. */
export function newTargetValues(
  type: TriggerTargetType,
  context: TargetDraftContext = { events: [], firesOnSchedule: false },
): TargetFormValues {
  const targetId = newTargetId();
  switch (type) {
    case TriggerTargetType.InvokeAction:
      return {
        type: TriggerTargetType.InvokeAction,
        targetId,
        spec: defaultInvocationSpecValues(),
        requiredInputs: [],
        requiredParameters: [],
        storedAdditionalInputs: [],
      };
    case TriggerTargetType.StartAgent:
      return {
        type: TriggerTargetType.StartAgent,
        targetId,
        agentId: null,
        variables: {},
        requiredVariables: [],
        visibility: ThreadVisibility.ORG,
        analysisScope: null,
      };
    case TriggerTargetType.SendSlackMessage:
      return {
        type: TriggerTargetType.SendSlackMessage,
        targetId,
        channelId: null,
        text: defaultSlackMessage(context),
      };
  }
}

/**
 * Seeds the form from a stored trigger.
 *
 * `timeZone` states the schedule's clock time; it defaults to the browser's,
 * which is the only zone a stored UTC cron can be read back into, and is a
 * parameter so a test can pin it.
 */
export function triggerRecordToFormValues(
  record: TriggerRecord,
  timeZone: string = browserTimeZone(),
): TriggerFormValues {
  const source = record.fires_on;
  const firesOn: TriggerFormValues["firesOn"] = isEventSubscription(source)
    ? {
        type: TriggerSourceType.Events,
        entity: subscriptionEntity(source.events),
        events: source.events,
        oncePer: source.once_per,
      }
    : {
        type: TriggerSourceType.Schedule,
        ...scheduleFromUtcCron(source.cron, timeZone),
      };
  const supplied = suppliedInputFor(firesOn);
  const condition = conditionToValues(record.condition);
  // A file trigger's patterns are a gate, which is a condition's job. They move
  // across only when the move is exact and there is somewhere to put them: a
  // condition the cards cannot show is kept whole, so the gate stays put.
  const rows = condition.type === "rows" ? condition.rows : null;
  const gate =
    supplied === "changed-file" && rows !== null
      ? gateConditionFor(sharedGateOf(record.targets.map(storedGate)) ?? [])
      : null;
  return {
    name: record.name,
    enabled: record.enabled,
    firesOn,
    condition:
      gate === null || rows === null
        ? condition
        : { type: "rows", rows: [...rows, gate] },
    targets: record.targets.map((spec) =>
      targetSpecToFormValues(spec, supplied, gate !== null),
    ),
  };
}

/**
 * The entity the form's Select shows. A subscription spanning entities has
 * none of its own, and `isEditableInForm` turns those away before the form is
 * ever built, so the first offered entity is only a placeholder.
 */
function subscriptionEntity(
  events: readonly PlatformEventType[],
): TriggerFormEntity {
  const entity = entityOfSubscription(events);
  return (
    TRIGGER_FORM_ENTITIES.find((offered) => offered === entity) ??
    TRIGGER_FORM_ENTITIES[0]
  );
}

/**
 * One stored target read back into the values its card edits.
 *
 * A lone accept-all pattern is how the builder spells "every file", so it
 * reads back as no pattern and the round trip does not double it. Beside
 * other patterns it widens them, and is kept.
 */
export function targetSpecToFormValues(
  spec: TriggerTargetSpec,
  supplied: ReturnType<typeof suppliedInputFor> = null,
  gateMoved = false,
): TargetFormValues {
  switch (spec.type) {
    case TriggerTargetType.InvokeAction:
      return {
        type: TriggerTargetType.InvokeAction,
        targetId: spec.target_id,
        spec: invocationSpecValuesOf(spec, supplied),
        // A dataset firing's patterns were the mount, which the invocation
        // block has read back as an input row. A file firing's are a gate, and
        // are kept only while they are still the gate -- once one has moved to
        // a condition, the form sends the open list the runtime needs.
        requiredInputs:
          supplied === "changed-file" && !gateMoved ? storedGate(spec) : [],
        // Known once the action loads; the card notes them then.
        requiredParameters: [],
        storedAdditionalInputs: spec.additional_inputs ?? [],
      };
    case TriggerTargetType.StartAgent:
      return {
        type: TriggerTargetType.StartAgent,
        targetId: spec.target_id,
        agentId: spec.agent_id,
        variables: { ...spec.values },
        // Known once the agent loads; the card notes them then.
        requiredVariables: [],
        visibility: spec.visibility,
        analysisScope: spec.analysis_scope ?? null,
      };
    case TriggerTargetType.SendSlackMessage:
      return {
        type: TriggerTargetType.SendSlackMessage,
        targetId: spec.target_id,
        channelId: spec.channel_id,
        text: spec.text,
      };
  }
}

/**
 * The patterns a stored target gates on. A lone accept-all is how the builder
 * spells "every file", not something anyone typed, so it reads back as none.
 */
function storedGate(spec: TriggerTargetSpec): string[] {
  if (spec.type !== TriggerTargetType.InvokeAction) {
    return [];
  }
  return spec.required_inputs.length === 1 &&
    spec.required_inputs[0] === ACCEPT_ALL_PATTERN
    ? []
    : spec.required_inputs;
}

/**
 * The shared invocation block a stored target holds, plus the input row a
 * dataset firing's old-style mount patterns now read back as.
 */
function invocationSpecValuesOf(
  spec: Extract<TriggerTargetSpec, { type: TriggerTargetType.InvokeAction }>,
  supplied: ReturnType<typeof suppliedInputFor>,
): InvocationSpecValues {
  const values = invocationSpecValuesFromWire(spec);
  return {
    ...values,
    // The request builder adds the triggering session back for a session
    // firing, so it isn't carried here, where a firing changed to another
    // event would save a placeholder nothing fills in.
    topicAndSessionSelectors: withoutTriggeringSession(
      values.topicAndSessionSelectors,
    ),
    // A dataset firing used to mount its files through `required_inputs`.
    // Those patterns are an input row now, so a trigger saved the old way
    // opens with the same files mounted and saves them the new way.
    inputs: [
      ...(supplied === "changed-dataset" && storedGate(spec).length > 0
        ? [{ source: "triggering-dataset" as const, globs: storedGate(spec) }]
        : []),
      ...values.inputs,
    ],
  };
}

function withoutTriggeringSession(
  selectors: InvocationSpecValues["topicAndSessionSelectors"],
): InvocationSpecValues["topicAndSessionSelectors"] {
  if (selectors === undefined || !isTriggeringSession(selectors.sessions)) {
    return selectors;
  }
  const { sessions: _triggering, ...rest } = selectors;
  return rest.topics === null || rest.topics === undefined ? undefined : rest;
}
